import store from './store/index'
import { SysConsoleProviderModule } from '@/system-modules/console/ConsoleProvider'
import { SysModManagerProviderModule } from '@/system-modules/mod-manager/ModManagerProvider'
import Window from '@/system-modules/win-manager/v1/core/entities/Window'
import { ISysWinManagerModule } from '@/contracts/system-modules/win-manager/ISysWinManagerModule'
import { ISysConsoleModule } from '@/contracts/system-modules/console/ISysConsoleModule'
import { ISysModManagerModule } from '@/contracts/system-modules/mod-manager/ISysModManagerModule'
import { IModule } from '@/contracts/core/module/interface/IModule'
import { EnumConsoleType } from '@/contracts/system-modules/console/enum/EnumConsoleType'
import { IStore } from './core/interface/IStore'
import { ISysAuthModule } from '@/contracts/system-modules/auth/ISysAuthModule'
import { ISysSyncModule } from '@/contracts/system-modules/sync/ISysSyncModule'
import { ISysShareModule } from '@/contracts/system-modules/share/ISysShareModule'
import { IEventBus } from '@/contracts/core/event/interface/IEventBus'
import { IRecordState } from '@/contracts/system-modules/sync/operation/IRecordState'
import { EnumSystemEvent } from '@/contracts/core/event/enum/EnumSystemEvent'
import { EnumOperationKind } from '@/contracts/system-modules/sync/enum/EnumOperationKind'
import { SysAuthProviderModule } from '@/system-modules/auth/SysAuthProvider'
import { SysSyncProviderModule } from '@/system-modules/sync/SysSyncProvider'
import { SysShareProviderModule } from '@/system-modules/share/SysShareProvider'
import { EventProviderModule } from '@/core/event/EventProvider'

/** Системный набор: открытый модуль каждого рабочего места, запись — UUID пользователя */
export const WORKSPACES_SET = 'workspaces'

export class WinManagerV1 implements ISysWinManagerModule {
  private store: IStore = store()
  private static instance: ISysWinManagerModule
  private console: ISysConsoleModule
  private modManager: ISysModManagerModule
  private events: IEventBus

  /**
   * auth, sync и share берутся при обращении: они создаются позже win-manager, а share сам
   * открывает окна через него.
   */
  constructor(
    private auth: () => ISysAuthModule = () => SysAuthProviderModule(),
    private sync: () => ISysSyncModule = () => SysSyncProviderModule(),
    private share: () => ISysShareModule = () => SysShareProviderModule(),
    events: IEventBus = EventProviderModule(),
  ) {
    this.console = SysConsoleProviderModule()
    this.modManager = SysModManagerProviderModule()
    this.events = events
  }

  getActiveModulePath(): string {
    if (!this.store.moduleId) {
      return ''
    }
    return this.modManager.get(this.store.moduleId).getViewPath()
  }

  getOpenWindowKey(): string {
    return this.store.moduleId
  }

  toggleWin(moduleId: string): void {
    try {
      const exist = this._searchWindow(moduleId)
      if (!exist) {
        const module = this.modManager.get(moduleId)
        this._createWindow(moduleId, module)
      }
      if (this.store.moduleId === moduleId) {
        this.store.moduleId = ''
      } else {
        this.store.moduleId = moduleId
      }
      this._saveOpenModule()
    } catch (e) {
      this.console.log(`Error toggling window for module ${moduleId}: ${(e as Error).message}`, EnumConsoleType.ERROR)
    }
  }

  openWin(moduleId: string): void {
    try {
      if (!this._searchWindow(moduleId)) {
        this._createWindow(moduleId, this.modManager.get(moduleId))
      }
      this.store.moduleId = moduleId
      this._saveOpenModule()
    } catch (e) {
      this.console.log(`Error opening window for module ${moduleId}: ${(e as Error).message}`, EnumConsoleType.ERROR)
    }
  }

  restoreWin(replace: boolean): void {
    if (!replace && this.store.moduleId !== '') {
      return
    }
    this._readSavedModule().then((moduleId) => this._showSaved(moduleId, replace))
  }

  getPanelModules(): { [key: string]: IModule } {
    return this.modManager.getAll()
  }

  getMainWinPath(): string {
    return this._viewPath() + 'main.vue'
  }

  getWidgetWinPath(): string {
    return this._viewPath() + 'widget.vue'
  }

  init(): boolean {
    this.events.on(EnumSystemEvent.UNLOCKED, () => this.restoreWin(false))
    this.events.on(EnumSystemEvent.WORKSPACE_CHANGED, () => this.restoreWin(true))
    this.console.log('WinManager module initialized', EnumConsoleType.SUCCESS)
    return true
  }

  static getInstance(): ISysWinManagerModule {
    if (!WinManagerV1.instance) {
      WinManagerV1.instance = new WinManagerV1()
    }
    return WinManagerV1.instance
  }

  _viewPath(): string {
    return '/win-manager/v1/view/'
  }

  _searchWindow(moduleId: string): boolean {
    return !!this.store.windows[moduleId]
  }

  _createWindow(moduleId: string, module: IModule): void {
    const window = new Window()
    window.setModule(module)
    this.store.windows[moduleId] = window
  }

  /** Гость по ссылке и вошедший без учётной записи рабочих мест не имеют */
  _hasWorkspace(): boolean {
    return this.auth().getUserUuid() !== '' && !this.share().isGuest()
  }

  _saveOpenModule(): void {
    if (!this._hasWorkspace()) {
      return
    }
    const userUuid = this.auth().getUserUuid()
    this.sync().write({
      set: WORKSPACES_SET,
      record: userUuid,
      kind: EnumOperationKind.PUT,
      event: null,
      fields: { [this.auth().getWorkspace()]: this.store.moduleId },
    })
  }

  async _readSavedModule(): Promise<string> {
    if (!this._hasWorkspace()) {
      return ''
    }
    const userUuid = this.auth().getUserUuid()
    const operations = await this.sync().getRecordOperations(WORKSPACES_SET, userUuid)
    const empty: IRecordState = { record: userUuid, fields: {}, times: {}, isRemoved: false }
    const state = operations.reduce((current, operation) => this.sync().mergeRecord(current, operation), empty)
    const moduleId = state.fields[this.auth().getWorkspace()]

    return typeof moduleId === 'string' ? moduleId : ''
  }

  /** Сохранённое не пишется обратно: оно уже в журнале. Модуля больше нет — пустая страница без ошибки */
  _showSaved(moduleId: string, replace: boolean): void {
    if (!replace && this.store.moduleId !== '') {
      return
    }
    if (moduleId === '' || !this._isKnownModule(moduleId)) {
      if (moduleId !== '') {
        this.console.log(`Saved module ${moduleId} is not in the system`, EnumConsoleType.WARNING)
      }
      this.store.moduleId = ''
      return
    }
    if (!this._searchWindow(moduleId)) {
      this._createWindow(moduleId, this.modManager.get(moduleId))
    }
    this.store.moduleId = moduleId
  }

  _isKnownModule(moduleId: string): boolean {
    return this.modManager.get(moduleId) !== undefined
  }
}

export default function (): ISysWinManagerModule {
  return WinManagerV1.getInstance()
}
